import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsAPI } from '../services/api';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  formatPrice: (amount: number) => string;
  setCurrency: (currency: string) => void;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

const currencyConfig = {
  TRY: { symbol: '₺', name: 'Türk Lirası' },
  EUR: { symbol: '€', name: 'Euro' },
  USD: { symbol: '$', name: 'Amerikan Doları' }
};

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<string>('TRY');
  const [currencySymbol, setCurrencySymbol] = useState<string>('₺');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCurrencySettings();
  }, []);

  const loadCurrencySettings = async () => {
    try {
      setLoading(true);
      const settings = await settingsAPI.getAll();
      
      // Settings artık object olarak geliyor, array değil
      const currencyValue = settings.currency;
      const symbolValue = settings.currencySymbol;
      
      if (currencyValue) {
        setCurrencyState(currencyValue);
      }
      
      if (symbolValue) {
        setCurrencySymbol(symbolValue);
      } else if (currencyValue) {
        // Eğer sembol ayarı yoksa, para biriminden otomatik belirle
        const config = currencyConfig[currencyValue as keyof typeof currencyConfig];
        if (config) {
          setCurrencySymbol(config.symbol);
        }
      }
    } catch (error) {
      // console.error('Para birimi ayarları yüklenemedi:', error);
      // Varsayılan değerleri kullan
      setCurrencyState('TRY');
      setCurrencySymbol('₺');
    } finally {
      setLoading(false);
    }
  };

  const setCurrency = async (newCurrency: string) => {
    try {
      const config = currencyConfig[newCurrency as keyof typeof currencyConfig];
      if (!config) {
        throw new Error('Geçersiz para birimi');
      }

      // Backend'e kaydet
      await settingsAPI.update('currency', newCurrency);
      await settingsAPI.update('currencySymbol', config.symbol);
      
      // State'i güncelle
      setCurrencyState(newCurrency);
      setCurrencySymbol(config.symbol);
    } catch (error) {
      // console.error('Para birimi güncellenemedi:', error);
      throw error;
    }
  };

  const formatPrice = (amount: number | string): string => {
    // String ise number'a çevir
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    if (isNaN(numAmount) || numAmount === null || numAmount === undefined) {
      return `${currencySymbol}0.00`;
    }

    const formattedAmount = numAmount.toFixed(2);
    
    // Para birimi pozisyonunu belirle
    switch (currency) {
      case 'USD':
      case 'EUR':
        return `${currencySymbol}${formattedAmount}`;
      case 'TRY':
      default:
        return `${currencySymbol}${formattedAmount}`;
    }
  };

  const value: CurrencyContextType = {
    currency,
    currencySymbol,
    formatPrice,
    setCurrency,
    loading
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export { currencyConfig };




